// @flow
import { selectTrack } from '../selectors/track';
import { AUDIO_QUALITY_LOSSLESS, QUEUE_TYPE_PLAYLIST, QUEUE_TYPE_ALBUM } from '../constants';

import type { SonosGroupsArray } from '../components/player/SonosGroups';
import type { SonosGroup } from '../components/player/SonosGroup';
import type { Request, ThunkAction } from './types';
import type { Track, SonosTrack } from '../shapes/types';

import { selectAccessToken } from '../selectors/auth';
import {
  selectElectronClientVersion,
  selectDeviceId,
  selectUseMP3EncryptedPlayer,
  selectUseM3U8EncryptedPlayer,
} from '../selectors/client';
import { selectSonosIsConnected } from '../selectors/sonos';
import { selectPlayerQueueOrigin } from '../selectors/player';
import { selectPlaylist } from '../selectors/playlist';
import { selectAlbum } from '../selectors/album';
import { selectQueueOriginCompoundId } from '../selectors/queue';

import { SonosQueue } from '../schema/index';
import { selectSonosTrackUrlResolver } from '../selectors/trackUrls';
import {
  IMGIX_STYLE_1,
  IMGIX_STYLE_2,
  PLAYABLE_ITEM_PREVIEW_DIMS,
  imgixImageUrl,
} from '../lib/imgix';

type ReplaceSonosGroupsAction = {
  type: 'REPLACE_SONOS_GROUPS',
  groups: SonosGroupsArray,
};

type PickSonosGroupAction = {
  type: 'PICK_SONOS_GROUP',
  group: SonosGroup,
};

export type SetConnectedSonosGroupAction = {
  type: 'SET_CONNECTED_SONOS_GROUP',
  groupId: string,
};

type SetSonosSessionIdAction = {
  type: 'SET_SONOS_SESSION_ID',
  sessionId: string,
};

export type SetSonosQueueAction = {
  type: 'SET_SONOS_QUEUE',
  tracks: Array<SonosTrack>,
} & Request;

type SetSonosQueueLoadedAction = {
  type: 'SET_SONOS_QUEUE_LOADED',
};

type UpdateSonosGroupNameAction = {
  type: 'UPDATE_SONOS_GROUP_NAME',
  groupName: string,
};

export type SonosAction =
  | ReplaceSonosGroupsAction
  | PickSonosGroupAction
  | SetConnectedSonosGroupAction
  | SetSonosQueueAction
  | SetSonosSessionIdAction
  | SetSonosQueueLoadedAction
  | UpdateSonosGroupNameAction;

export function replaceSonosGroups(groups: SonosGroupsArray): ReplaceSonosGroupsAction {
  return {
    type: 'REPLACE_SONOS_GROUPS',
    groups,
  };
}

export function pickSonosGroup(group: SonosGroup): PickSonosGroupAction {
  return {
    type: 'PICK_SONOS_GROUP',
    group,
  };
}

export type PickSonosGroup = typeof pickSonosGroup;

export function setConnectedSonosGroup(groupId: string): SetConnectedSonosGroupAction {
  return {
    type: 'SET_CONNECTED_SONOS_GROUP',
    groupId,
  };
}

const createTrackToSonosTrack = (state: Object, quality: number) => {
  return async (trackId: string | number): Promise<SonosTrack> => {
    const stringId = trackId.toString();
    const track: Track = selectTrack(state, stringId, true);

    const queueOrigin = selectPlayerQueueOrigin(state);

    let imageUrl = imgixImageUrl({
      path: `/artists/${track.recording.work.composer.id}/main.jpg`,
      style: IMGIX_STYLE_1,
      width: PLAYABLE_ITEM_PREVIEW_DIMS.w,
      height: PLAYABLE_ITEM_PREVIEW_DIMS.h,
      dpr: 1,
    });

    if (queueOrigin.type === QUEUE_TYPE_PLAYLIST) {
      const playlist = selectPlaylist(state, queueOrigin.id);
      imageUrl = imgixImageUrl({
        path: playlist.imageUrl,
        style: IMGIX_STYLE_2,
        width: PLAYABLE_ITEM_PREVIEW_DIMS.w,
        height: PLAYABLE_ITEM_PREVIEW_DIMS.h,
        dpr: 1,
      });
    }

    if (queueOrigin.type === QUEUE_TYPE_ALBUM) {
      const album = selectAlbum(state, queueOrigin.id);
      imageUrl = imgixImageUrl({
        path: album.imageUrl,
        style: IMGIX_STYLE_2,
        width: PLAYABLE_ITEM_PREVIEW_DIMS.w,
        height: PLAYABLE_ITEM_PREVIEW_DIMS.h,
        dpr: 1,
      });
    }
    const trackUrlResolver = selectSonosTrackUrlResolver(quality);
    const trackUrl = await trackUrlResolver({
      id: stringId,
      quality,
      accessToken: selectAccessToken(state),
      clientVersion: selectElectronClientVersion(state),
      deviceId: selectDeviceId(state),
      sonosIsConnected: selectSonosIsConnected(state),
      useMP3EncryptedPlayer: selectUseMP3EncryptedPlayer(state),
      useM3U8EncryptedPlayer: selectUseM3U8EncryptedPlayer(state),
    });
    return {
      id: stringId,
      deleted: false,
      track: {
        name: track.piece.title,
        album: {
          name: track.recording.work.title,
        },
        artist: {
          name: track.recording.work.composer.name,
        },
        mediaUrl: trackUrl,
        contentType: quality === AUDIO_QUALITY_LOSSLESS ? 'audio/flac' : 'application/x-mpegURL',
        durationMillis: track.duration * 1000,
        imageUrl,
        type: 'track',
      },
    };
  };
};

export function setSonosQueue(
  tracks: Array<string | number>,
  quality: number = AUDIO_QUALITY_LOSSLESS
): ThunkAction {
  return async (dispatch, getState) => {
    const state = getState();
    const unResolvedTracks = tracks.map(createTrackToSonosTrack(state, quality));
    const sonosTracks = await Promise.all(unResolvedTracks);
    const cacheId = selectQueueOriginCompoundId(state);
    return dispatch({
      type: 'SET_SONOS_QUEUE',
      tracks: sonosTracks,
      IDAGIO_REQUEST: {
        type: 'API_REQUEST',
        method: 'POST',
        endpoint: '/v2/sonos/cloud_queue',
        body: {
          tracks: sonosTracks,
        },
      },
      meta: {
        restricted: true,
        schema: SonosQueue,
        normalize: result => {
          return {
            id: cacheId,
            cloudQueueId: result.id,
            tracks: sonosTracks,
          };
        },
      },
      cacheId,
    });
  };
}

export function setSonosSessionId(sessionId: string): SetSonosSessionIdAction {
  return {
    type: 'SET_SONOS_SESSION_ID',
    sessionId,
  };
}

export function updateSonosGroupName(groupName: string): UpdateSonosGroupNameAction {
  return {
    type: 'UPDATE_SONOS_GROUP_NAME',
    groupName,
  };
}
